import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

type User = {
  id: number;
  email: string | null;
  label: string | null;
  isAdmin: boolean;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  tokenMask: string;
  ratelimit: { evaluatePerDay: number; scanPerDay: number };
  today: { evaluatePerDay: number; scanPerDay: number };
  counts: { reports: number; applications: number; jobs: number };
};

async function getJSON<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: 'include' });
  if (r.status === 401) throw new Error('UNAUTH');
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

function fmtDate(s: string | null) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toISOString().slice(0, 10);
}

export default function AdminUsers() {
  const qc = useQueryClient();
  const meQ = useQuery({
    queryKey: ['session'],
    queryFn: () => getJSON<{ isAdmin: boolean; email: string }>('/api/session'),
    retry: false,
  });

  const usersQ = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => getJSON<{ users: User[]; day: string }>('/api/admin/users'),
    enabled: !!meQ.data?.isAdmin,
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const r = await fetch(`/api/admin/users/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'delete failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const toggleAdmin = useMutation({
    mutationFn: async ({ id, isAdmin }: { id: number; isAdmin: boolean }) => {
      const r = await fetch(`/api/admin/users/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_admin: isAdmin }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'patch failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  if (meQ.isLoading) return <div style={{ padding: 40 }}>Loading…</div>;
  // Unauth → straight to login (was bouncing to "/" which loops and confuses users).
  if (meQ.isError) return <Navigate to="/login?next=/admin/users" replace />;
  if (!meQ.data?.isAdmin) return <Navigate to="/" replace />;

  const users = usersQ.data?.users || [];
  const meEmail = meQ.data.email;

  return (
    <div style={{ maxWidth: 1200, margin: '40px auto', padding: '0 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h1 className="serif" style={{ fontSize: 32, fontWeight: 400, margin: 0 }}>Users</h1>
        <Link to="/admin/invites" style={{ fontSize: 13, color: 'var(--pri)' }}>Invite codes →</Link>
      </div>
      <p style={{ color: 'var(--ink2)', marginBottom: 32, fontSize: 14 }}>
        {users.length} user{users.length === 1 ? '' : 's'}. Today: {usersQ.data?.day || '—'}
      </p>

      {usersQ.isLoading && <div>Loading users…</div>}
      {usersQ.isError && <div style={{ color: '#b91c1c' }}>Failed to load users.</div>}

      {users.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: 'var(--bg)' }}>
              <tr>
                <th style={th}>Email</th>
                <th style={th}>Label</th>
                <th style={th}>Admin</th>
                <th style={th}>Verified</th>
                <th style={th}>Last login</th>
                <th style={th}>Joined</th>
                <th style={th}>Today (eval/scan)</th>
                <th style={th}>Reports</th>
                <th style={th}>Apps</th>
                <th style={th}>Jobs</th>
                <th style={{ ...th, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.email === meEmail;
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--line)' }}>
                    <td style={td}>
                      {u.email || <span style={dim}>—</span>}
                      {isMe && <span style={mePill}> you</span>}
                    </td>
                    <td style={td}>{u.label || <span style={dim}>—</span>}</td>
                    <td style={td}>
                      <button
                        disabled={isMe || toggleAdmin.isPending}
                        onClick={() => {
                          if (confirm(`${u.isAdmin ? 'Revoke admin from' : 'Grant admin to'} ${u.email}?`))
                            toggleAdmin.mutate({ id: u.id, isAdmin: !u.isAdmin });
                        }}
                        style={{
                          padding: '2px 8px', fontSize: 11, borderRadius: 4, border: '1px solid var(--line)',
                          background: u.isAdmin ? '#dbeafe' : '#fff',
                          color: u.isAdmin ? '#1e40af' : 'var(--ink2)',
                          cursor: isMe ? 'not-allowed' : 'pointer', opacity: isMe ? 0.5 : 1,
                        }}>
                        {u.isAdmin ? 'admin' : '—'}
                      </button>
                    </td>
                    <td style={td}>
                      {u.emailVerifiedAt
                        ? <span style={{ color: '#059669' }}>✓</span>
                        : <span style={{ color: '#dc2626' }}>✗</span>}
                    </td>
                    <td style={td}>{fmtDate(u.lastLoginAt)}</td>
                    <td style={td}>{fmtDate(u.createdAt)}</td>
                    <td style={td}>
                      {u.today.evaluatePerDay}/{u.ratelimit.evaluatePerDay}
                      &nbsp;·&nbsp;
                      {u.today.scanPerDay}/{u.ratelimit.scanPerDay}
                    </td>
                    <td style={td}>{u.counts.reports}</td>
                    <td style={td}>{u.counts.applications}</td>
                    <td style={td}>{u.counts.jobs}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        disabled={isMe || remove.isPending}
                        onClick={() => {
                          if (!isMe && confirm(`Permanently delete ${u.email}? This wipes all their reports, applications, jobs, and uploaded CVs.`))
                            remove.mutate(u.id);
                        }}
                        className="btn"
                        style={{
                          fontSize: 12, color: '#b91c1c',
                          cursor: isMe ? 'not-allowed' : 'pointer', opacity: isMe ? 0.4 : 1,
                        }}
                        title={isMe ? "You can't delete yourself" : 'Permanently delete user + all data'}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        <Link to="/" style={{ color: 'var(--pri)', fontSize: 13 }}>← Back to app</Link>
      </div>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontWeight: 600, fontSize: 12, color: 'var(--ink2)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const td: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const dim: React.CSSProperties = { color: 'var(--ink3)' };
const mePill: React.CSSProperties = { marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 999, background: '#fef3c7', color: '#92400e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' };
